import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { PieChart } from "@mui/x-charts/PieChart";
import { ChartContainer } from "@mui/x-charts/ChartContainer";
import { BarPlot } from "@mui/x-charts/BarChart";
import { LinePlot, MarkPlot } from "@mui/x-charts/LineChart";
import { ChartsXAxis } from "@mui/x-charts/ChartsXAxis";
import { ChartsYAxis } from "@mui/x-charts/ChartsYAxis";
import { ChartsGrid } from "@mui/x-charts/ChartsGrid";
import { ChartsReferenceLine } from "@mui/x-charts/ChartsReferenceLine";
import { ChartsTooltip } from "@mui/x-charts/ChartsTooltip";
import {
  CustomLoading,
  CustomSectionTitle,
  CustomSelect,
} from "@ippon/shared-components";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { fetchBilling, fetchDailyUsage } from "../../../store/aiUsageSlice";
import { recentMonths } from "./aiUsageApi";
import { fetchContractStatus } from "../../../api/contractApi";
import { useAiUsageAccess } from "@/hooks/useAiUsageAccess";

// ===== カラーパレット（基調2色: rgb(11,157,189) / rgb(219,248,255) + アカウント契約用グレー） =====
const C_DARK = "#0B9DBD"; // rgb(11, 157, 189)
const C_LIGHT = "#DBF8FF"; // rgb(219, 248, 255)
const C_EST = C_DARK; // 円グラフ AI-BPO スライス
const C_CHAT = C_LIGHT; // 円グラフ IPPON スライス / 推移グラフの棒
const C_ACCOUNT = "#B0BEC5"; // 円グラフ アカウント契約スライス
const C_PROPERTY = "#FFB74D"; // 円グラフ 物件登録数スライス
const C_PREPAY = "#7E57C2"; // 請求一覧 年払い一括（前払い）スタック
const C_CUSTOM = "#26A69A"; // 任意項目（フォールバック色）
// 任意請求項目（DynamoDBのlabel）を1件ずつ色分けするためのパレット。
// 既存の区分色（C_PREPAY/C_ACCOUNT/C_PROPERTY/C_EST/C_CHAT）と重ならない色を選ぶ。
const CUSTOM_COLORS = ["#26A69A", "#8D6E63", "#5C6BC0", "#EC407A", "#66BB6A", "#FF7043", "#26C6DA"];
const C_CUM = C_DARK; // 推移グラフの折れ線・無料枠ライン
const C_COUNT = "#E8833A"; // 推移グラフ 折れ線＝累積回数/クレジット（右軸）

const BORDER = "1px solid #E0E0E0";
const MUTED = "#6B7280";
const INK = "#354053";

// ===== 機能名称（集計方法が異なる2系統） =====
const LABEL_BPO = "工事見積査定AI";
const LABEL_BPO_SHORT = "工事見積査定AI";
const LABEL_AGENT = "IPPON AIエージェント";
const LABEL_AGENT_SHORT = "AIエージェント";

// ===== 料金体系（API未取得時のみ使うフォールバック定数。実値は必ず API summary を優先） =====
// 各単価はクライアントごとに上書きされ得るため、通常の描画では summary.xxxJpy を使う。
// ここに残っているのは「APIレスポンスがまだ無い（初回ロード中）」瞬間のプレースホルダー用途のみ。
const AI_BPO_INCLUDED_RUNS = 100; // 基本料金に含まれる実行回数
const AI_BPO_BASE_JPY = 55000; // 基本料金
const AI_BPO_PRICE_PER_RUN = 550; // 超過1回あたり
const CREDIT_PER_USER = 1000; // 1席あたりの付与クレジット
const AGENT_PRICE_PER_CREDIT = 3; // ¥/クレジット（無料超過分のみ課金）
const ACCOUNT_PRICE_PER_USER = 6600; // 1アカウントあたりの料金（1名目から課金・基本枠なし）
const PROPERTY_PRICE_PER_UNIT = 3300; // 物件登録数（PROPERTY_COUNT）：1棟あたり ¥3,300（税込・従量。段階制PROPERTY_TIERは廃止）

// 白いパネル
const Panel: React.FC<{
  title: string;
  hint?: string;
  children: React.ReactNode;
  action?: React.ReactNode;
}> = ({ title, hint, children, action }) => (
  <Box
    sx={{
      background: "#fff",
      border: BORDER,
      borderRadius: "8px",
      p: { xs: "14px 14px", sm: "18px 20px" },
      mt: "18px",
    }}
  >
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        mb: "14px",
        gap: 1,
        flexWrap: "wrap",
      }}
    >
      <Typography
        sx={{
          fontSize: ".98rem",
          fontWeight: 700,
          color: INK,
          display: "flex",
          alignItems: "baseline",
          gap: "8px",
          flexWrap: "wrap",
        }}
      >
        {title}
        {hint && (
          <Box component="span" sx={{ fontSize: ".76rem", color: MUTED, fontWeight: 400 }}>
            {hint}
          </Box>
        )}
      </Typography>
      {action}
    </Box>
    {children}
  </Box>
);

// サマリーカード
const SumCard: React.FC<{
  title: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
}> = ({ title, value, sub }) => (
  <Box
    sx={{
      background: "#fff",
      border: BORDER,
      borderRadius: "8px",
      p: "16px 18px",
      height: "100%",
      minHeight: "150px",
      boxSizing: "border-box",
    }}
  >
    <Box sx={{ fontSize: ".8rem", color: MUTED, mb: "8px" }}>{title}</Box>
    <Box sx={{ fontSize: "1.5rem", fontWeight: 700, color: INK, lineHeight: 1.15 }}>
      {value}
    </Box>
    {sub && <Box sx={{ fontSize: ".72rem", color: MUTED, mt: "6px" }}>{sub}</Box>}
  </Box>
);

// 課金カード（無料/基本枠 と「超過分 or 残り」を明示）
const BillingCard: React.FC<{
  title: string;
  billed?: string; // 請求額
  usageRatio?: { used: number; limit?: number; limitLabel?: string; unit: string }; // 使用回数 / 上限（例: 158回 / 100）。limit/limitLabel 未指定なら分母を表示しない（例: 基本シート0）
  usedText?: string; // 使用量（例: 60,000クレジット使用）
  quotaLabel?: string; // 枠の説明（例: 無料枠 30,000クレジット）
  quotaAmount?: string; // 枠内の料金（例: ¥0）
  secondLabel?: string; // 超過 or 残り のラベル
  secondAmount?: string; // 超過料金 or 残り回数/クレジット
  secondAccent?: boolean; // true=超過（強調）, false=残り（中立）
}> = ({ title, billed, usageRatio, usedText, quotaLabel, quotaAmount, secondLabel, secondAmount, secondAccent }) => (
  <Box
    sx={{
      background: "#fff",
      border: BORDER,
      borderRadius: "8px",
      p: "16px 18px",
      height: "100%",
      minHeight: "150px",
      boxSizing: "border-box",
      display: "flex",
      flexDirection: "column",
    }}
  >
    <Box sx={{ fontSize: ".8rem", color: MUTED, mb: "6px" }}>{title}</Box>
    <Box
      sx={{
        display: "flex",
        alignItems: "baseline",
        gap: "10px",
        flexWrap: "wrap",
        minWidth: 0,
      }}
    >
      {billed && (
        <Box sx={{ fontSize: "1.5rem", fontWeight: 700, color: INK, lineHeight: 1.15 }}>
          {billed}
        </Box>
      )}
      {usageRatio && (
        <Box sx={{ display: "flex", alignItems: "baseline", flexWrap: "wrap", gap: "4px", minWidth: 0, maxWidth: "100%" }}>
          <Box
            component="span"
            sx={{ fontSize: billed ? "1.1rem" : "1.5rem", fontWeight: 700, color: INK, lineHeight: 1.15, whiteSpace: "nowrap" }}
          >
            {usageRatio.used.toLocaleString()}
          </Box>
          <Box component="span" sx={{ fontSize: ".7rem", fontWeight: 400, color: MUTED, lineHeight: 1.15, whiteSpace: "nowrap" }}>
            {usageRatio.limitLabel != null || usageRatio.limit != null
              ? ` / ${usageRatio.limitLabel ?? usageRatio.limit?.toLocaleString()}${usageRatio.unit}`
              : usageRatio.unit}
          </Box>
        </Box>
      )}
    </Box>
    {usedText && <Box sx={{ fontSize: ".72rem", color: MUTED, mt: "4px" }}>{usedText}</Box>}
    {/* 枠内（無料/基本）と「超過 or 残り」を明示（カード下部に揃える） */}
    <Box sx={{ mt: "auto", pt: "10px", display: "flex", flexDirection: "column", gap: "4px" }}>
      {quotaLabel && quotaAmount && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: "8px",
            py: "5px",
            borderRadius: "6px",
            background: "#F3F8FC",
          }}
        >
          <Box component="span" sx={{ fontSize: ".72rem", color: MUTED }}>
            {quotaLabel}
          </Box>
          <Box component="span" sx={{ fontSize: ".78rem", fontWeight: 700, color: INK }}>
            {quotaAmount}
          </Box>
        </Box>
      )}
      {secondLabel && secondAmount && (
        <Box
          sx={{
            display: "flex",
            // 超過は「ラベル → 改行 → 金額」の縦並び、残りは横並び
            flexDirection: secondAccent ? "column" : "row",
            justifyContent: secondAccent ? "flex-start" : "space-between",
            alignItems: secondAccent ? "flex-start" : "center",
            gap: secondAccent ? "2px" : 0,
            px: "8px",
            py: "5px",
            borderRadius: "6px",
            ...(secondAccent
              ? { background: C_LIGHT, border: `1px solid ${C_DARK}` }
              : { background: "#F3F8FC" }),
          }}
        >
          <Box
            component="span"
            sx={{ fontSize: ".72rem", color: secondAccent ? C_DARK : MUTED, fontWeight: secondAccent ? 600 : 400 }}
          >
            {secondLabel}
          </Box>
          <Box
            component="span"
            sx={{ fontSize: ".82rem", fontWeight: 700, color: secondAccent ? C_DARK : INK }}
          >
            {secondAmount}
          </Box>
        </Box>
      )}
    </Box>
  </Box>
);

// 推移グラフの料金は既に「円」なので換算せずそのまま表示
const yenTick = (v: number) => "¥" + Math.round(v).toLocaleString();
// BarChart の series.valueFormatter 用（null を許容）
const yenSeries = (v: number | null) =>
  v == null ? "" : "¥" + Math.round(v).toLocaleString();

// 累積配列
const cumsum = (arr: number[]): number[] => {
  let run = 0;
  return arr.map((v) => {
    run += v;
    return run;
  });
};

const AiUsageSetting: React.FC = () => {
  const dispatch = useAppDispatch();
  const { canAccess, loading: loadingAccess } = useAiUsageAccess();
  const {
    daily: apiDaily,
    billing: apiBilling,
    loadingDaily,
    loadingBilling,
    error,
  } = useAppSelector((state) => state.aiUsage);

  const now = useMemo(() => new Date(), []);
  // 契約開始日（対象月プルダウンの下限）。実APIから取得（後段の useEffect で設定）。
  const [contractStartDate, setContractStartDate] = useState<string | null>(
    null
  );
  // 対象月の選択肢: 契約開始月〜当月（契約開始日が取れない間は直近12ヶ月をフォールバック）。
  const monthOptions = useMemo(() => {
    const curYear = now.getFullYear();
    const curMonth = now.getMonth() + 1;
    let count = 12;
    if (contractStartDate) {
      const start = new Date(contractStartDate);
      const startYear = start.getFullYear();
      const startMonth = start.getMonth() + 1;
      // 契約開始月〜当月の月数（最低1・未来開始でも当月は表示）。
      count = Math.max(
        1,
        (curYear - startYear) * 12 + (curMonth - startMonth) + 1
      );
    }
    return recentMonths(curYear, curMonth, count).map(({ year, month }) => ({
      value: `${year}-${month}`,
      label: `${year}年${month}月`,
    }));
  }, [now, contractStartDate]);
  const [selected, setSelected] = useState<string>(
    `${now.getFullYear()}-${now.getMonth() + 1}`
  );

  const [year, month] = selected.split("-").map(Number);

  useEffect(() => {
    if (!canAccess || loadingAccess) return;
    dispatch(fetchDailyUsage({ year, month }));
  }, [canAccess, dispatch, loadingAccess, year, month]);

  useEffect(() => {
    if (!canAccess || loadingAccess) return;
    dispatch(fetchBilling({ months: 12 }));
  }, [canAccess, dispatch, loadingAccess]);

  // 契約満了日（契約更新日の表示用）。実APIから取得し、未取得時はダミー表示
  const [contractEndDate, setContractEndDate] = useState<string | null>(null);
  useEffect(() => {
    if (!canAccess || loadingAccess) return;
    let active = true;
    fetchContractStatus()
      .then((s) => {
        if (active) {
          setContractEndDate(s.contractEndDate);
          setContractStartDate(s.contractStartDate);
        }
      })
      .catch(() => {
        /* 401(CONTRACT_EXPIRED) は axios interceptor が強制ログアウト */
      });
    return () => {
      active = false;
    };
  }, [canAccess, loadingAccess]);
  const renewalDateLabel = contractEndDate
    ? new Date(contractEndDate)
        .toLocaleDateString("ja-JP", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        })
        .replace(/-/g, "/")
    : "—";

  // ===== 実データ（集計テーブル）を表示 =====
  // 日次は日次集計テーブル、請求一覧は月次集計テーブルから取得した API 値をそのまま使う。
  // 対象月の集計がまだ無い場合は空（0 / 未集計表示）になる。
  const daily = apiDaily;
  const billing = apiBilling;
  // aggregatedAt === null は「対象月の集計行が1件も無い」ことを意味する
  const noDailyData =
    !loadingDaily && (!apiDaily || apiDaily.aggregatedAt === null);

  const summary = daily?.summary;
  const chartsLoading = loadingDaily || loadingBilling;

  // ===== 課金体系の実値（summary 優先・未取得時は既定値） =====
  // 単価はクライアントごとに上書きされ得るため、必ず summary の解決済み値を使う
  // （FE固定の定数は「APIレスポンス到着前の一瞬」だけのフォールバック）。
  const bpoIncludedRuns = summary?.bpoIncludedRuns ?? AI_BPO_INCLUDED_RUNS;
  const bpoBaseJpy = summary?.bpoBaseJpy ?? AI_BPO_BASE_JPY;
  const bpoPerRunPrice = summary?.bpoPerRunJpy ?? AI_BPO_PRICE_PER_RUN;
  const userCount = summary?.userCount ?? 0;
  const creditPerUser = summary?.creditPerUser ?? CREDIT_PER_USER;
  const freeAgentCredits = summary?.freeAgentCredits ?? userCount * creditPerUser;
  const seatUnitPrice = summary?.seatUnitPriceJpy ?? ACCOUNT_PRICE_PER_USER;
  const propertyUnitPrice = summary?.propertyUnitPriceJpy ?? PROPERTY_PRICE_PER_UNIT;
  const creditUnitPrice = summary?.creditUnitPriceJpy ?? AGENT_PRICE_PER_CREDIT;

  // ===== 推移グラフ（累積使用量・累積料金。実データから算出） =====
  const { bpoCountCum, bpoBillCum, creditCum, agentBillCum } = useMemo(() => {
    const estCount = daily?.estCount ?? [];
    const chCredits = daily?.ch_credits ?? [];
    const bpoCountCum = cumsum(estCount);
    const creditCum = cumsum(chCredits).map((v) => Math.round(v));
    // AI-BPO: 基本料金（有効月まで）＋ 無料枠超過分 × 超過単価（利用が一度でもあれば基本料金発生）
    const bpoBillCum = bpoCountCum.map((runs) =>
      runs > 0
        ? bpoBaseJpy + Math.max(0, runs - bpoIncludedRuns) * bpoPerRunPrice
        : 0
    );
    // IPPON: 無料枠を超えた累積分 × 単価（無料枠内は0）
    const agentBillCum = creditCum.map(
      (credit) => Math.max(0, credit - freeAgentCredits) * creditUnitPrice
    );
    return { bpoCountCum, bpoBillCum, creditCum, agentBillCum };
  }, [daily, bpoBaseJpy, bpoIncludedRuns, bpoPerRunPrice, freeAgentCredits, creditUnitPrice]);

  // ===== 当月の請求額（正本 = API summary。billedJpy はアカウント契約込みの合計） =====
  const bpoBilled = summary?.bpoBilledJpy ?? 0;
  const agentBilled = summary?.agentBilledJpy ?? 0;
  // アカウント契約: 1名目から課金・基本枠なし（summary 優先・未取得時はフォールバック算出）
  const accountBilled = summary?.accountBilledJpy ?? userCount * seatUnitPrice;
  // 物件登録数: 1棟あたり従量（summary 優先・未取得時はフォールバック算出）
  const propertyCount = summary?.propertyCount ?? 0;
  const propertyBilled =
    summary?.propertyBilledJpy ?? propertyCount * propertyUnitPrice;
  // 年払い一括前払い（annual_lump契約の課金開始月のみ発生）。内訳の詳細は別画面で表示するため、ここでは合計額のみ使用
  const annualPrepayBilled = summary?.annualPrepayBilledJpy ?? 0;
  // 任意請求項目（初期導入等・DynamoDB billing-custom-items）。当月合計・税込 ＋ 明細（label 別に表示）
  const customBilled = summary?.customBilledJpy ?? 0;
  const customItems = summary?.customItems ?? [];
  const totalBilled =
    summary?.billedJpy ??
    accountBilled + bpoBilled + agentBilled + propertyBilled + customBilled;
  // 内税控除（消費税は税込合計から算出。API 未取得時は 10% で算出）
  const taxBilled =
    summary?.taxJpy ?? totalBilled - Math.floor(totalBilled / 1.1);

  // 無料枠／超過分・残り（カード表示用）
  const bpoUsedRuns = summary?.estRunCnt ?? 0;
  const bpoOverRuns = Math.max(0, bpoUsedRuns - bpoIncludedRuns);
  const bpoOverCost = bpoOverRuns * bpoPerRunPrice;
  const bpoRemainRuns = summary?.remainBpoRuns ?? 0;
  const bpoIsOver = bpoOverRuns > 0;
  const agentUsedCredits = Math.round(summary?.totCredits ?? 0);
  const agentOverCredits = Math.max(0, agentUsedCredits - freeAgentCredits);
  const agentOverCost = agentOverCredits * creditUnitPrice;
  const agentRemainCredits = summary?.remainAgentCredits ?? 0;
  const agentIsOver = agentOverCredits > 0;
  const accountOverUsers = userCount;
  const accountOverCost = accountOverUsers * seatUnitPrice;
  const accountIsOver = accountOverUsers > 0;

  // 任意請求項目の label 一覧（直近12ヶ月に出現する全 label を重複なく収集）。
  // グラフ・凡例は「その他」でまとめず、DynamoDB の label ごとに分けて表示する。
  const customLabels = useMemo(() => {
    const set = new Set<string>();
    (billing?.items ?? []).forEach((b) =>
      (b.customItems ?? []).forEach((it) => set.add(it.label))
    );
    return Array.from(set);
  }, [billing]);
  // label → 色（当月カードの円グラフと12ヶ月グラフで色を揃える）
  const customColor = useMemo(() => {
    const m: Record<string, string> = {};
    customLabels.forEach((L, i) => {
      m[L] = CUSTOM_COLORS[i % CUSTOM_COLORS.length];
    });
    return (label: string) => m[label] ?? C_CUSTOM;
  }, [customLabels]);

  // ===== 請求一覧（直近12ヶ月の月別請求金額・内訳付き・実データ） =====
  const billing12 = useMemo(() => {
    const items = billing?.items ?? [];
    return [...items]
      .sort((a, b) => a.year * 12 + a.month - (b.year * 12 + b.month)) // 古い→新しい
      .map((b) => ({
        label: `${b.month}月`,
        prepay: b.annualPrepayBilledJpy ?? 0, // 年払い一括（基本料金×12・契約開始月のみ）
        // 任意項目は label ごとに月額を持つ（「その他」でまとめない）
        customByLabel: customLabels.reduce((acc, L) => {
          acc[L] = (b.customItems ?? [])
            .filter((it) => it.label === L)
            .reduce((s, it) => s + it.amountJpy, 0);
          return acc;
        }, {} as Record<string, number>),
        account: b.accountBilledJpy, // アカウント契約（月払いの月次基本料金）
        property: b.propertyBilledJpy, // 物件登録数
        bpo: b.bpoBilledJpy, // AI-BPO（工事見積査定AI）
        bpoContracted: b.bpoContracted, // その月に AI-BPO オプション契約中か
        agent: b.agentBilledJpy, // IPPON AIエージェント
        total: b.totalBilledJpy,
      }));
  }, [billing, customLabels]);

  // AI-BPO（オプション）契約中か。未契約はカード/円グラフ/推移をトルツメ（SPEC §5-7）
  const bpoOptionActive = summary?.bpoOptionActive ?? false;

  // 積み上げ棒の系列の表示可否（データ／契約が無い区分はグラフに出さない）:
  //  - 年払い一括 / 任意項目（label別）: 金額が全月0なら非表示
  //  - AI-BPO（オプション）: いずれかの月で契約中なら表示、全月未契約なら非表示（トルツメ）
  const showPrepay = useMemo(() => billing12.some((b) => (b.prepay ?? 0) > 0), [billing12]);
  const showBpo = useMemo(() => billing12.some((b) => b.bpoContracted), [billing12]);
  // 12ヶ月で1件でも金額がある任意項目 label のみグラフ系列に出す
  const shownCustomLabels = useMemo(
    () =>
      customLabels.filter((L) =>
        billing12.some((b) => (b.customByLabel[L] ?? 0) !== 0)
      ),
    [customLabels, billing12]
  );

  // 集計情報テキスト（日次バッチは毎日 01:10 JST に前日分を確定）
  const aggInfoText = useMemo(() => {
    if (loadingDaily) return "";
    if (noDailyData || !daily?.aggregatedAt) return "";
    const dt = new Date(daily.aggregatedAt);
    const fmt = `${dt.getFullYear()}/${String(dt.getMonth() + 1).padStart(2, "0")}/${String(
      dt.getDate()
    ).padStart(2, "0")}`;
    return `集計日: ${fmt}（前日分まで反映）`;
  }, [daily, noDailyData, loadingDaily]);

  if (loadingAccess) {
    return (
      <Box
        pb={3}
        pt={{ xs: 2, sm: 3 }}
        sx={{
          minHeight: "calc(100vh - 120px)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <CustomSectionTitle title="利用状況" size="large" />
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CustomLoading loading fullScreen={false} size="medium" />
        </Box>
      </Box>
    );
  }
  if (!canAccess) {
    return (
      <Box pb={3} pt={{ xs: 2, sm: 3 }}>
        <CustomSectionTitle title="利用状況" size="large" />
        <Box
          sx={{
            mt: "18px",
            background: "#fff",
            border: BORDER,
            borderRadius: "8px",
            p: "40px 20px",
            textAlign: "center",
            color: MUTED,
            fontSize: ".9rem",
          }}
        >
          この画面は「物件管理」を利用中のクライアントに所属する、アカウント権限が「オーナー」のアカウントのみ閲覧できます。
        </Box>
      </Box>
    );
  }

  return (
    <Box
      pb={3}
      pt={{ xs: 2, sm: 3 }}
      sx={{
        minHeight: "calc(100vh - 120px)",
        display: "flex",
        flexDirection: "column",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      <CustomSectionTitle title="利用状況" size="large" />

      {/* 集計情報（前日分まで反映。契約更新日の上に表示） */}
      <Box component="span" sx={{ display: "block", fontSize: "14px", color: MUTED, mt: "6px" }}>
        {aggInfoText}
      </Box>

      {/* 契約更新日（実API: 契約満了日。未取得時は「—」表示） */}
      <Box component="span" sx={{ display: "block", fontSize: "14px", color: MUTED, mt: "8px" }}>
        契約更新日: {renewalDateLabel}
      </Box>

      {/* ツールバー */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexWrap: "wrap",
          background: "#fff",
          border: BORDER,
          borderRadius: "8px",
          p: "12px 16px",
          mt: 1.5,
        }}
      >
        <CustomSelect
          name="month"
          label="対象月"
          options={monthOptions}
          defaultValue={selected}
          onChange={(v: string) => setSelected(v)}
          width="140px"
          XsWidth="120px"
        />
        {error && (
          <Box component="span" sx={{ fontSize: ".76rem", color: "#C0392B" }}>
            {error}
          </Box>
        )}
      </Box>

      {chartsLoading ? (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CustomLoading loading fullScreen={false} size="medium" />
        </Box>
      ) : (
        <>
          {/* 概要サマリー */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(186px, 1fr))",
              gap: "14px",
              mt: "18px",
            }}
          >
            <SumCard
              title="請求額 / 月（税込）"
              value={yenTick(totalBilled)}
              sub={`うち消費税 ${yenTick(taxBilled)}（内税）／ アカウント契約 ${yenTick(accountBilled)} / 物件登録数 ${yenTick(propertyBilled)}${bpoOptionActive ? ` / ${LABEL_BPO_SHORT} ${yenTick(bpoBilled)}` : ""} / ${LABEL_AGENT_SHORT} ${yenTick(agentBilled)}${customItems.map((it) => ` / ${it.label} ${yenTick(it.amountJpy)}`).join("")}${annualPrepayBilled > 0 ? ` / 年払い一括 ${yenTick(annualPrepayBilled)}` : ""}`}
            />
        <BillingCard
          title="アカウント数"
          // 基本シート0：分母（枠の人数）は一旦非表示（limit を渡さない）。1名目から課金
          usageRatio={{ used: userCount, unit: "人" }}
          secondLabel="課金"
          secondAmount={`${yenTick(accountOverCost)}（${accountOverUsers.toLocaleString()}人×¥${seatUnitPrice.toLocaleString()}）`}
          secondAccent={accountIsOver}
        />
        <BillingCard
          title="物件登録数"
          // 1棟 ¥3,300 の従量化により上限（分母）は不要 → 棟数のみ表示
          usageRatio={{ used: propertyCount, unit: "棟" }}
          secondLabel="課金"
          secondAmount={`${yenTick(propertyBilled)}（${propertyCount.toLocaleString()}棟×¥${propertyUnitPrice.toLocaleString()}）`}
          secondAccent={true}
        />
        <BillingCard
          title={`${LABEL_AGENT} / 月`}
          usageRatio={{
            used: agentUsedCredits,
            limit: freeAgentCredits,
            limitLabel: `${creditPerUser.toLocaleString()} × ${userCount}`,
            unit: "クレジット",
          }}
          secondLabel={agentIsOver ? "超過" : "残り"}
          secondAmount={
            agentIsOver
              ? `${yenTick(agentOverCost)}（${agentOverCredits.toLocaleString()}×¥${creditUnitPrice}）`
              : `${agentRemainCredits.toLocaleString()} クレジット`
          }
          secondAccent={agentIsOver}
        />
        {/* AI-BPO はオプション。未契約はカードごとトルツメ（SPEC §5-7） */}
        {bpoOptionActive && (
          <BillingCard
            title={`${LABEL_BPO_SHORT} / 月`}
            usageRatio={{ used: bpoUsedRuns, limit: bpoIncludedRuns, unit: "回" }}
            secondLabel={bpoIsOver ? "超過" : "残り"}
            secondAmount={
              bpoIsOver
                ? `${yenTick(bpoOverCost)}（${bpoOverRuns.toLocaleString()}回×¥${bpoPerRunPrice}）`
                : `${bpoRemainRuns.toLocaleString()}回`
            }
            secondAccent={bpoIsOver}
          />
        )}
          </Box>

      {/* 料金比率（年払い一括 / アカウント契約 / AI-BPO / IPPON AIエージェント） */}
      <Panel title="料金比率" hint="当月の請求額内訳（基本料金・アカウント契約・物件登録数込み／年払いは一括前払い分を含む）">
        <RatioPieChart
          items={[
            // 年払い一括（annual_lump契約の課金開始月のみ発生）。金額があるときだけ表示
            ...(annualPrepayBilled > 0
              ? [{ label: "年払い一括", value: annualPrepayBilled, color: C_PREPAY }]
              : []),
            { label: "アカウント契約", value: accountBilled, color: C_ACCOUNT },
            { label: "物件登録数", value: propertyBilled, color: C_PROPERTY },
            // AI-BPO はオプション未契約なら円グラフからも除外（トルツメ）
            ...(bpoOptionActive
              ? [{ label: LABEL_BPO, value: bpoBilled, color: C_EST }]
              : []),
            { label: LABEL_AGENT, value: agentBilled, color: C_CHAT },
            // 任意項目は「その他」でまとめず、DynamoDB の label ごとにスライスを分ける
            // （円グラフは比率のため、割引=マイナス額は除外し金額が正の項目のみ）
            ...customItems
              .filter((it) => it.amountJpy > 0)
              .map((it) => ({
                label: it.label,
                value: it.amountJpy,
                color: customColor(it.label),
              })),
          ]}
          formatter={yenTick}
        />
      </Panel>

      <Panel
        title={`${LABEL_AGENT} 料金の推移`}
        hint={`棒グラフ＝累積料金（左軸 ¥）／ 折れ線＝累積クレジット（右軸）。無料枠 ${freeAgentCredits.toLocaleString()}クレジットを超えた分のみ課金`}
      >
        {daily && freeAgentCredits > 0 ? (
          <FeatureTrendChart
            labels={daily.labels}
            bars={[{ data: agentBillCum, label: "累積料金", color: C_DARK }]}
            line={{ data: creditCum, label: "累積クレジット", color: C_COUNT }}
            leftFormatter={yenTick}
            rightFormatter={(v) => Math.round(v).toLocaleString()}
            leftLabel="料金 (¥)"
            rightLabel="累積クレジット"
            freeLine={{
              axis: "right",
              value: freeAgentCredits,
              label: `無料枠 ${freeAgentCredits.toLocaleString()}クレジットまで`,
            }}
          />
        ) : (
          <EmptyChart />
        )}
      </Panel>

      {/* AI-BPO はオプション。未契約は「料金の推移」グラフごとトルツメ（SPEC §5-7） */}
      {bpoOptionActive && (
        <Panel
          title={`${LABEL_BPO} 料金の推移`}
          hint={`棒グラフ＝累積料金（左軸 ¥）／ 折れ線＝累積回数（右軸）。${bpoIncludedRuns}回まで基本料金${yenTick(bpoBaseJpy)}・超過1回 ¥${bpoPerRunPrice}`}
        >
          {daily ? (
            <FeatureTrendChart
              labels={daily.labels}
              bars={[{ data: bpoBillCum, label: "累積料金", color: C_DARK }]}
              line={{ data: bpoCountCum, label: "累積回数", color: C_COUNT }}
              leftFormatter={yenTick}
              rightFormatter={(v) => `${Math.round(v).toLocaleString()}回`}
              leftLabel="料金 (¥)"
              rightLabel="累積回数"
              freeLine={{
                axis: "right",
                value: bpoIncludedRuns,
                label: `基本料金 ${bpoIncludedRuns}回まで`,
              }}
            />
          ) : (
            <EmptyChart />
          )}
        </Panel>
      )}

      {/* 請求一覧（直近12ヶ月の月別請求金額・積み上げ棒グラフで内訳表示）— 一番下に配置 */}
      <Panel title="請求一覧" hint="直近12ヶ月の請求金額（月別・内訳の積み上げ／年払いは契約開始月に一括分を表示）">
        {billing12.length > 0 ? (
          <Box sx={{ height: 320 }}>
            <BarChart
              height={320}
              xAxis={[{ data: billing12.map((b) => b.label), scaleType: "band", height: 40 }]}
              yAxis={[
                {
                  width: 80,
                  max: Math.max(...billing12.map((b) => b.total), 0) * 1.2 || undefined,
                  valueFormatter: (v: number) => "¥" + Math.round(v).toLocaleString(),
                },
              ]}
              series={[
                // 年払い一括: データが無い（全月0）なら非表示
                ...(showPrepay
                  ? [
                      {
                        data: billing12.map((b) => b.prepay),
                        label: "年払い一括",
                        color: C_PREPAY,
                        stack: "billing",
                        valueFormatter: yenSeries,
                      },
                    ]
                  : []),
                // 任意項目: 「その他」でまとめず、DynamoDB の label ごとに積み上げ系列を分ける
                // （全月0の label は出さない）
                ...shownCustomLabels.map((L) => ({
                  data: billing12.map((b) => b.customByLabel[L] ?? 0),
                  label: L,
                  color: customColor(L),
                  stack: "billing",
                  valueFormatter: yenSeries,
                })),
                {
                  data: billing12.map((b) => b.account),
                  label: "アカウント契約",
                  color: C_ACCOUNT,
                  stack: "billing",
                  valueFormatter: yenSeries,
                },
                {
                  data: billing12.map((b) => b.property),
                  label: "物件登録数",
                  color: C_PROPERTY,
                  stack: "billing",
                  valueFormatter: yenSeries,
                },
                // AI-BPO（オプション）: オプション未契約=全月0なら非表示（トルツメ）
                ...(showBpo
                  ? [
                      {
                        data: billing12.map((b) => b.bpo),
                        label: LABEL_BPO_SHORT,
                        color: C_EST,
                        stack: "billing",
                        valueFormatter: yenSeries,
                      },
                    ]
                  : []),
                {
                  data: billing12.map((b) => b.agent),
                  label: LABEL_AGENT_SHORT,
                  color: C_CHAT,
                  stack: "billing",
                  valueFormatter: yenSeries,
                  // 最上段の系列に「棒全体の合計金額」をバーの上（外側）に表示
                  barLabel: (item) =>
                    item.dataIndex == null
                      ? ""
                      : yenSeries(billing12[item.dataIndex]?.total ?? null),
                  barLabelPlacement: "outside",
                },
              ]}
              sx={{ "& .MuiBarLabel-root": { fontSize: 11, fontWeight: 700, fill: INK } }}
              margin={{ top: 48, right: 24, bottom: 24, left: 8 }}
            />
          </Box>
        ) : (
          <EmptyChart />
        )}
      </Panel>
        </>
      )}
    </Box>
  );
};

// データなし／ローディング表示（チャート枠）
const EmptyChart: React.FC = () => (
  <Box
    sx={{
      height: 200,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: MUTED,
      fontSize: 13,
      gap: 1,
    }}
  >
    データなし
  </Box>
);

// 比率ドーナツ円グラフ（アカウント契約 / AI-BPO / IPPON AIエージェント等）。料金・利用回数で再利用。
const RatioPieChart: React.FC<{
  items: { label: string; value: number; color: string }[];
  formatter: (v: number) => string;
}> = ({ items, formatter }) => {
  const total = items.reduce((sum, it) => sum + it.value, 0);
  if (total <= 0) {
    return (
      <Box
        sx={{
          height: 120,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: MUTED,
          fontSize: 13,
        }}
      >
        データなし
      </Box>
    );
  }
  return (
    <Box>
      {/* 組み込み凡例は狭幅で見切れるため非表示にし、下にカスタム凡例を表示。中央には合計値を表示 */}
      <Box sx={{ height: 220, position: "relative" }}>
        <PieChart
          height={220}
          hideLegend
          series={[
            {
              data: items.map((it, i) => ({
                id: i,
                value: it.value,
                label: it.label,
                color: it.color,
              })),
              innerRadius: 50,
              paddingAngle: 2,
              cornerRadius: 4,
              valueFormatter: (item) => formatter(item.value),
              highlightScope: { fade: "global", highlight: "item" },
            },
          ]}
          margin={{ top: 6, bottom: 6, left: 6, right: 6 }}
        />
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
            pointerEvents: "none",
          }}
        >
          <Box sx={{ fontSize: ".68rem", color: MUTED }}>合計</Box>
          <Box sx={{ fontSize: "1.05rem", fontWeight: 700, color: INK, whiteSpace: "nowrap" }}>
            {formatter(total)}
          </Box>
        </Box>
      </Box>
      {/* カスタム凡例（折り返し対応・色／名称／実数値／比率） */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: "6px 16px",
          mt: "6px",
        }}
      >
        {items.map((it) => (
          <Box key={it.label} sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "2px",
                background: it.color,
                flexShrink: 0,
              }}
            />
            <Box component="span" sx={{ fontSize: ".78rem", color: INK }}>
              {it.label}
            </Box>
            <Box component="span" sx={{ fontSize: ".78rem", fontWeight: 700, color: INK }}>
              {formatter(it.value)}
            </Box>
            <Box component="span" sx={{ fontSize: ".72rem", color: MUTED }}>
              ({Math.round((it.value / total) * 100)}%)
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// 推移チャート（棒[左軸:料金 ¥] + 折れ線[右軸:回数/クレジット] + 無料枠ライン）。feature 毎に再利用。
const FeatureTrendChart: React.FC<{
  labels: string[];
  bars: { data: number[]; label: string; color: string }[]; // 左軸（料金）
  line: { data: number[]; label: string; color?: string }; // 右軸（回数/クレジット）
  leftFormatter: (v: number) => string;
  rightFormatter: (v: number) => string;
  leftLabel?: string; // 左軸タイトル
  rightLabel?: string; // 右軸タイトル
  freeLine: { axis: "left" | "right"; value: number; label: string };
  height?: number;
}> = ({
  labels,
  bars,
  line,
  leftFormatter,
  rightFormatter,
  leftLabel,
  rightLabel,
  freeLine,
  height = 330,
}) => {
  const barColor = bars[0]?.color ?? C_DARK;
  const lineColor = line.color ?? C_CUM;
  return (
    <Box>
      {/* 凡例：棒グラフ／折れ線が それぞれ どちらの軸か を明示 */}
      <Box
        sx={{
          display: "flex",
          gap: "18px",
          flexWrap: "wrap",
          alignItems: "center",
          fontSize: ".72rem",
          color: INK,
          mb: "6px",
        }}
      >
        <Box component="span" sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <Box component="span" sx={{ width: 12, height: 12, bgcolor: barColor, borderRadius: "2px" }} />
          <span>
            <strong>棒グラフ</strong>＝{bars[0]?.label}（<span style={{ color: barColor, fontWeight: 700 }}>左軸 {leftLabel}</span>）
          </span>
        </Box>
        <Box component="span" sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <Box component="span" sx={{ width: 18, height: 0, borderTop: `3px solid ${lineColor}` }} />
          <span>
            <strong>折れ線</strong>＝{line.label}（<span style={{ color: lineColor, fontWeight: 700 }}>右軸 {rightLabel}</span>）
          </span>
        </Box>
      </Box>
      <Box sx={{ height }}>
        <ChartContainer
          height={height}
          xAxis={[{ data: labels, scaleType: "band", id: "x", height: 40 }]}
          yAxis={[
            {
              id: "left",
              position: "left",
              width: 80,
              label: leftLabel,
              valueFormatter: (v: number) => leftFormatter(v),
              ...(freeLine.axis === "left" ? { min: 0, max: freeLine.value * 2 } : {}),
            },
            {
              id: "right",
              position: "right",
              width: 88,
              label: rightLabel,
              valueFormatter: (v: number) => rightFormatter(v),
              // 無料枠が縦軸のちょうど中央に来るよう、max を無料枠の2倍に固定
              ...(freeLine.axis === "right" ? { min: 0, max: freeLine.value * 2 } : {}),
            },
          ]}
          series={[
            ...bars.map((b) => ({
              type: "bar" as const,
              data: b.data,
              label: b.label,
              stack: "c",
              color: b.color,
              yAxisId: "left",
            })),
            {
              type: "line" as const,
              data: line.data,
              label: line.label,
              color: lineColor,
              yAxisId: "right",
              curve: "linear",
            },
          ]}
          margin={{ top: 10, right: 12, bottom: 24, left: 8 }}
        >
          <ChartsGrid horizontal />
          <BarPlot />
          <LinePlot />
          <MarkPlot />
          <ChartsReferenceLine
            axisId={freeLine.axis}
            y={freeLine.value}
            label={freeLine.label}
            labelAlign="start"
            lineStyle={{ stroke: lineColor, strokeDasharray: "5 4", strokeWidth: 1.5 }}
            // 棒グラフと重ならないよう線の少し上へ寄せ、白い縁取りで棒の上でも読めるようにする
            labelStyle={{
              fill: lineColor,
              fontSize: 11,
              fontWeight: 700,
              stroke: "#ffffff",
              strokeWidth: 3.5,
              paintOrder: "stroke",
              transform: "translateY(-9px)",
            }}
          />
          <ChartsXAxis axisId="x" />
          <ChartsYAxis
            axisId="left"
            labelStyle={{ fill: barColor, fontSize: 11, fontWeight: 700 }}
          />
          <ChartsYAxis
            axisId="right"
            labelStyle={{ fill: lineColor, fontSize: 11, fontWeight: 700 }}
          />
          <ChartsTooltip />
        </ChartContainer>
      </Box>
    </Box>
  );
};

export default AiUsageSetting;
